// @ts-check
import { log } from '../../utils/console.js';
import * as helper from '../helper.js';
import * as ccService from '../cc-service.js';
import {
  convertToPx,
  isDesktop,
  isEdgeOrIE,
  isSafari,
  getValueByNs,
  onTabletToDesktop,
  onDesktopMediaChange,
} from '../../utils/index.js';
import { HTTP_STATUS_CODE } from '../../constants/constants.js';
import { addParams } from '../../utils/http.js';
import { createElement } from '../../helpers/dom-elements.js';
import * as modalInterface from './modal-interface.js';
import {
  getProductsUnavailableOnline,
  findProductsInCartById,
  generateSpecificProductsObject,
} from './service.js';
import { getZIPCodeInitialValue } from '../zip-code-service.js';
import ZIPCodeInput from '../zip-code-input.js';

/**
 * Pure JS Commerce Connector (aka Channel Sight) module. It implements cart transfer related logic.
 * Find the nearest dealer by user zip code module.
 *
 * @author Oleg Vaka, Nikolay Borzov
 */

/**
 * @typedef {object} CartTransferOptions
 * @property {string} locale
 * @property {string} createURL
 * @property {string} transferURL
 * @property {string} apiKey
 * @property {string} market
 * @property {string} [notDistributedCtaURL]
 * @property {boolean} [useZipCode=true] Show zip code field on the modal [Show zip code field].
 * @property {boolean} [enableEmptyZipCode=true] Allow run search if empty zip code appears [Show results for the whole
 * country if zip code is empty].
 * @property {boolean} [detectZipCode=true] Try to automatically detect zip code and use it in search request
 * [Enable geolocation (to use\not to use zip code by DEMANDBASE)].
 * @property {number} [zipCodeLength=20] [Maximum number of characters for a zip code].
 * @property {RegExp} [zipCodePattern='.*'] [Pattern for a zip code].
 * @property {boolean} [searchOnOpen=true] Run search on modal window open
 * [!Display results only after Search button is clicked].
 * @property {boolean} [closeOnEsc=true] If need to close modal on esc press.
 * @property {boolean} [isB2C=false]
 * @property {boolean} [showRedirectWarning=false] [Enable notification that after clicking on 'Buy Now' button a user
 * will be redirected to the distributor's website].
 */

/**
 * @typedef {object} CartTransferLabels
 * @property {string} ct_labels_modal_header
 * @property {string} ct_labels_search_title
 * @property {string} ct_labels_search_placeholder
 * @property {string} ct_labels_search_button
 * @property {string} ct_labels_no_results
 * @property {string} ct_labels_buy_cta_button
 * @property {string} ct_labels_order_cta_button
 * @property {string} ct_labels_details_title
 * @property {string} ct_labels_quantity
 * @property {string} ct_labels_all_available_in_stock
 * @property {string} ct_labels_not_all_available_in_stock
 * @property {string} ct_labels_not_distributed
 * @property {string} ct_labels_out_of_stock
 * @property {string} ct_labels_expand_details
 * @property {string} ct_labels_buy_online_btn_label
 * @property {string} ct_labels_not_distributed_online
 * @property {string} ct_labels_not_distributed_cta
 * @property {string} ct_labels_not_distributed_cta_link
 * @property {string} ct_labels_expand_not_distributed
 * @property {string} ct_labels_zip_input_placeholder
 */

/**
 * @typedef {object} CartTransferMessages
 * @property {string} ct_messages_not_distributed
 * @property {string} ct_messages_invalid_zip_code_error
 * @property {string} ct_messages_all_distributors_shown
 * @property {string} ct_messages_auto_detected_zip_code
 * @property {string} ct_messages_redirect_warning
 */

const STATUS_SUCCESS = 'Success';
const STATUS_CT_FAILED = 'FAILED';
const STATUS_CT_INVALID_REQUEST = 'INVALID_REQUEST';
const STATUS_CT_NO_RETAILERS = 'NO_RETAILERS_AVAILABLE';
const STATUS_CT_NO_PRODUCTS = 'NO_PRODUCTS_AVAILABLE_AT_SELECTED_RETAILERS';
const METHODS = ['GET', 'POST'];

/** @type {ZIPCodeInput} */
let zipCodeInput = ZIPCodeInput.getNullObject();

/** @type {CartTransferLabels} */
let _labels;
/** @type {CartTransferMessages} */
let _messages;
let _productsInCart;
/** @type {CartTransferOptions} */
let _options;
let _channelSightData;
let _unavailableProductsObject;

let _clCode; // country-LANGUAGE code e.g. fr-FR

const MAX_PRODUCTS_IN_ROW = 3;

function checkViewUnavailableState() {
  if (!document.querySelector('.js-view-more-unavailable')) {
    return;
  }

  const notAvailableBody = document.querySelector('.js-retail-not-available');
  const notAvailableItemsListContainer = notAvailableBody.querySelector(
    '.retail-not-available__items',
  );

  if (!isDesktop()) {
    notAvailableItemsListContainer.style.maxHeight = '';
  } else if (
    !notAvailableBody.classList.contains('is-expanded') &&
    !notAvailableItemsListContainer.style.maxHeight &&
    notAvailableItemsListContainer.dataset.height
  ) {
    notAvailableItemsListContainer.style.maxHeight = convertToPx(
      notAvailableItemsListContainer.dataset.height,
    );
  }
}

function findProduct(product) {
  return product.commRef === this.commRef;
}

function mergeCartProducts(products) {
  const mergedProducts = products.reduce((mergedProducts, product) => {
    const index = mergedProducts.findIndex(findProduct, product);

    if (index !== -1) {
      mergedProducts[index].qty += product.qty;
    } else {
      mergedProducts.push(product);
    }

    return mergedProducts;
  }, []);

  return mergedProducts;
}

/**
 * @param {CartTransferOptions} options
 * @param {object} labels
 * @param {object} messages
 * @param {Array} products
 */
export function init(options, labels, messages, products) {
  _options = options;
  _clCode = _options.locale;
  _labels = labels;
  _messages = messages;

  if (products) {
    _productsInCart = mergeCartProducts(products);
  }
}

function getProductsAndQuantity(products) {
  const result = products.map((product) => ({
    pid: product.commRef,
    qty: product.qty,
  }));

  return result;
}

export function getRetailers() {
  const zipCode = _options.useZipCode
    ? getZIPCodeInitialValue({
        countryLanguageCode: _options.locale,
        detect: _options.detectZipCode,
        length: _options.zipCodeLength,
        pattern: _options.zipCodePattern,
      })
    : '';

  return cartCreateRequest({
    products: getProductsAndQuantity(_productsInCart),
    zipCode,
  }).then((result) => result.data);
}

function getChannelSightRetailerQuantity(retailer) {
  const productStatuses = [
    'ProductsCarriedInStock',
    'ProductsCarriedOutOfStock',
    'ProductsNotCarried',
  ];

  return productStatuses.reduce(
    (result, status) => result + getValueByNs(retailer, `${status}.length`, 0),
    0,
  );
}

// TODO:CartTransfer Remove when channelsight will fix its response data
function fixChannelSightResponse(retailers) {
  const productsInCard = getProductsAndQuantity(_productsInCart);

  retailers.forEach((retailer) => {
    if (productsInCard.length !== getChannelSightRetailerQuantity(retailer)) {
      retailer.ProductsNotCarried = [];
      const availableInStockProductsAtRetailer = generateSpecificProductsObject(
        retailer.ProductsCarriedInStock,
      );
      const availableOutOfStockProductsAtRetailer = generateSpecificProductsObject(
        retailer.ProductsCarriedOutOfStock,
      );

      const availableProductsAtRetailer = {
        ...availableInStockProductsAtRetailer,
        ...availableOutOfStockProductsAtRetailer,
      };

      productsInCard.forEach((product) => {
        if (!availableProductsAtRetailer.hasOwnProperty(product.pid)) {
          retailer.ProductsNotCarried.push({ ProductId: product.pid, Quantity: product.qty });
        }
      });
    }
  });

  return retailers;
}

/**
 * @param {object} params
 * @param {*[]} params.products
 * @param {string} params.zipCode
 * @returns {Promise}
 */
function cartCreateRequest({ products, zipCode }) {
  if (products.length === 0) {
    log.error('products are expected');
  }

  const { host } = window.location;
  const www = 'www.';

  return ccService
    .createCartV1dot11(_options.createURL, {
      key: _options.apiKey,
      mk: _options.market,
      lang: _clCode,
      cl: zipCode || undefined,
      pdata: products,
      csi_wtbsource: host.indexOf(www) === 0 ? host.replace(www, '') : host,
    })
    .then((responseData) => {
      const { data } = responseData;
      const hasRetailers =
        data.RetailerCarts &&
        data.RetailerCarts.some(
          (retailer) =>
            getValueByNs(retailer, 'ProductsCarriedInStock.length', 0) > 0 ||
            getValueByNs(retailer, 'ProductsCarriedOutOfStock.length', 0) > 0,
        );

      const result = responseData;

      if (hasRetailers) {
        _channelSightData = data;
        _channelSightData.RetailerCarts = fixChannelSightResponse(_channelSightData.RetailerCarts);
      }

      return result;
    })
    .catch(searchError);
}

/**
 * Validates cart transfer response.
 *
 * @function
 * @name checkIfResponseIsValid
 * @param {object} responseData Response data.
 * @returns {boolean} Cart transfer response validation status.
 */
function checkIfResponseIsValid(responseData) {
  // Validate fields
  if (
    !responseData.Status ||
    !responseData.Method ||
    !responseData.Action ||
    !responseData.Action.length
  ) {
    return false;
  }

  // Validate values
  if (responseData.Status !== STATUS_SUCCESS || !METHODS.includes(responseData.Method)) {
    return false;
  }

  // Validate Post request
  if (responseData.Method === 'POST') {
    return !!(getValueByNs(responseData, 'Parameters.length', 0) && responseData.RetailerCartId);
  }

  return true;
}

/**
 * Toggles button spinner.
 *
 * @function
 * @name toggleButtonSpinner
 * @param {HTMLElement} button Button where spinner is toggled.
 */
function toggleButtonSpinner(button) {
  button.disabled = !button.hasAttribute('disabled');
}

/**
 * Opens new blank window.
 *
 * @returns {Window | null} Popup - Window object representing the newly created window, or null if it cannot be
 * opened.
 */
function createNewWindow() {
  let popup;

  // Edge and IE11 browsers require specific params for the new window
  if (isEdgeOrIE()) {
    const IEParamsObject = {
      top: 0,
      left: 0,
      width: window.screen.width,
      height: window.screen.height,
      fullscreen: true,
      directories: true,
      location: true,
      resizable: true,
      menubar: true,
      toolbar: true,
      scrollbars: true,
      status: true,
    };

    const popupParams = Object.keys(IEParamsObject).reduce((paramString, paramKey) => {
      const paramValue = IEParamsObject[paramKey];
      let renderedValue = paramValue;

      if (typeof paramValue === 'boolean') {
        renderedValue = paramValue ? 'yes' : 'no';
      }

      return `${paramString}${paramKey}=${renderedValue},`;
    }, '');

    popup = window.open('', '_blank', popupParams);
  } else {
    popup = window.open('', '_blank');
  }

  return popup;
}

/**
 * Makes cart transfer request.
 *
 * @name cartTransferRequest
 * @param {string} cartID Cart id to be transferred.
 * @param {HTMLElement} button Button initiated transfer request.
 * @returns {Promise<boolean>}
 */
function cartTransferRequest(cartID, button) {
  toggleButtonSpinner(button);

  return ccService
    .transferCartV1dot11(_options.transferURL, {
      key: _options.apiKey,
      retailerCartID: cartID,
      pdata: getProductsAndQuantity(_productsInCart) || undefined,
    })
    .then((responseData) => {
      const { data } = responseData;
      const result = checkIfResponseIsValid(data);

      if (result) {
        transferCartToRetailer(
          data.Method,
          data.Action,
          data.RetailerCartId,
          getValueByNs(data, 'Parameters', []),
        );
      }

      toggleButtonSpinner(button);

      return !!result;
    })
    .catch((data) => {
      toggleButtonSpinner(button);
      searchError(data);
    });
}

/**
 * Redirects to a retailer cart.
 *
 * @param {string} method Method used in redirect request.
 * @param {string} url URL used for making a redirect request.
 * @param {string} retailerCartID Used for creating form.
 * @param {Array} [params] Optional params.
 */
function transferCartToRetailer(method, url, retailerCartID, params) {
  if (method === 'GET') {
    // new window is required for redirecting to the retailer in new tab
    const redirectionPopup = createNewWindow();

    let paramsObj = {};

    // Convert Rexel retailer format params array to a key-value format
    // if the migration to the GET method is done
    // We don't create form with GET method to avoid loosing params in the provided url
    // as the form submitting removes them
    if (getValueByNs(params, 'length', 0)) {
      paramsObj = params.reduce((paramsAccumulator, param) => {
        const paramKey = Object.keys(param)[0];

        paramsAccumulator[paramKey] = param[paramKey];
        return paramsAccumulator;
      }, {});
    }

    url = addParams(url, paramsObj);

    const isPopupBlocked = helper.checkIfPopupIsBlocked(redirectionPopup);

    // If popups are blocked — open retailer cart website in current tab
    if (isPopupBlocked) {
      document.window.location = url;
    } else {
      redirectionPopup.location = url;
    }
  } else if (method === 'POST') {
    // Rexel retailer uses POST method (September 2019)
    // Date of the migration to the GET method is unknown

    params?.push({ RetailerCartID: retailerCartID });

    /** @type {HTMLFormElement} */
    const form = createElement('form', {
      attributes: {
        action: url,
        method,
        // Safari blocks `form` submission whet it has `target` attribute
        ...(!isSafari() && { target: '_blank' }),
      },
      contents: getValueByNs(params, 'length', 0)
        ? params.map((param) => {
            // Rexel retailer provides params in a specific format:
            // an array of objects with key as a name, and value as a value
            // [{name1: 'value1'}, {name2: 'value2'}]
            const [name, value] = Object.entries(param)[0];

            return createElement('input', {
              type: 'hidden',
              attributes: {
                name,
                value,
              },
            });
          })
        : null,
    });

    document.body.appendChild(form);

    form.submit();
    console.log(form);
    form.parentNode.removeChild(form);
  }
}

/**
 * @typedef {object} Product
 * @property {string} id
 * @property {string} commercialReference
 * @property {string} description
 * @property {string} imageURL
 */

/**
 * Init and open modal window.
 * Start search if options.
 *
 * @param {Product[]} products
 * @param {object} cartTransferResponseData
 */
export function open(products, cartTransferResponseData) {
  if (products) {
    _productsInCart = [];
    _productsInCart = mergeCartProducts(products);
  }

  _channelSightData = { ...cartTransferResponseData };

  modalInterface.openModal({
    options: _options,
    labels: _labels,
    messages: _messages,
  });

  if (_options.useZipCode) {
    zipCodeInput = new ZIPCodeInput(
      modalInterface.getZipCodeInput(),
      {
        countryLanguageCode: _options.locale,
        pattern: _options.zipCodePattern,
        length: _options.zipCodeLength,
        detect: _options.detectZipCode,
      },
      {
        onChange: ({ isValid }) => {
          if (isValid) {
            modalInterface.hideValidationMessage();
          }
        },
      },
    );

    modalInterface.getSearchForm().addEventListener('submit', (e) => {
      e.preventDefault();

      if (zipCodeInput.isValid()) {
        modalInterface.hideValidationMessage();
        startSearch();
      } else {
        modalInterface.showValidationMessage();
      }
    });

    modalInterface.hideValidationMessage();
  }

  modalInterface.clearDistributorsList();

  if (_options.searchOnOpen || !_options.useZipCode) {
    startSearch();
  }
}

/**
 * Check if search should be disabled.
 *
 * @returns {boolean}
 */
function isSearchDisabled() {
  // Disable search if empty zip code forbidden, but zip code is empty
  // The option matters only if user can see and change zip code.
  return _options.useZipCode && !_options.enableEmptyZipCode && zipCodeInput.isEmpty();
}

/**
 * Start search and save user zip code if it possible.
 */
function startSearch() {
  if (isSearchDisabled()) {
    return;
  }

  modalInterface.clearDistributorsList();
  modalInterface.showLoader();
  zipCodeInput.save();

  cartCreateRequest({
    products: getProductsAndQuantity(_productsInCart),
    zipCode: zipCodeInput.getValue(),
  }).then(searchSuccess, searchError);
}

/**
 * Get maximum height of the provided elements.
 *
 * @function getMaximumHeight
 * @param {Array} elements Array of Elements.
 * @returns {number} Maximum height of the elements.
 */
function getMaximumHeight(elements) {
  return Math.max(...elements.map((element) => element.getBoundingClientRect().height), 0);
}

// TODO: Move this to `modal-interface.js`
function toggleNotAvailableItems(notAvailableItemsContainer) {
  const notAvailableBody = document.querySelector('.js-retail-not-available');
  const notAvailableItemsList = document.querySelectorAll('.retail-not-available__item');

  // get maximum height of the first elements in a row
  const maxHeight = getMaximumHeight([...notAvailableItemsList].slice(0, MAX_PRODUCTS_IN_ROW));

  notAvailableItemsContainer.style.maxHeight = convertToPx(maxHeight);
  notAvailableItemsContainer.dataset.height = maxHeight;

  // show/hide more unavailable items (with index more than MAX_PRODUCTS_IN_ROW value)
  notAvailableBody.addEventListener('click', (e) => {
    if (e.target && e.target.matches('.js-view-more-unavailable')) {
      notAvailableBody.classList.toggle('is-expanded');
      if (!notAvailableBody.classList.contains('is-expanded')) {
        notAvailableItemsContainer.dataset.height = maxHeight;
        notAvailableItemsContainer.style.maxHeight = convertToPx(maxHeight);
      } else {
        notAvailableItemsContainer.style.maxHeight = '';
        delete notAvailableItemsContainer.dataset;
      }
    }
  });
}

/**
 * Search success handler. Shows messages and dealer list.
 *
 * @param {object} responseData
 * @param {object} responseData.data
 */
function searchSuccess({ data }) {
  if (!data) {
    log.warn('Status is Success, but no data was received');
  }

  modalInterface.clearDistributorsList();

  _channelSightData = { ...data };

  if (data.CartId) {
    if (data.RetailerCarts) {
      // If zip code is not shown, then
      // there is no need to save zip code
      if (_options.useZipCode) {
        modalInterface.showZipCodeAlertMessage({
          zipCode: zipCodeInput.getValue(),
          isDetected: zipCodeInput.isDetected(),
          messages: {
            allDistributorsShown: _messages.ct_messages_all_distributors_shown,
            autoDetectedZipCode: _messages.ct_messages_auto_detected_zip_code,
          },
        });
      }

      if (_options.showRedirectWarning) {
        modalInterface.showRedirectWarning(_messages.ct_messages_redirect_warning);
      }

      _unavailableProductsObject = getProductsUnavailableOnline(_channelSightData.RetailerCarts);

      const unavailableProductIds = Object.keys(_unavailableProductsObject);

      const unavailableProductsArray = findProductsInCartById(
        unavailableProductIds,
        _productsInCart,
      );

      modalInterface.fillRetailerRows({
        channelSightData: _channelSightData,
        unavailableProductsObject: _unavailableProductsObject,
        productsInCart: _productsInCart,
        options: _options,
        labels: _labels,
        messages: _messages,
        callbacks: {
          onCartTransferButtonClick: cartTransferRequest,
        },
      });

      if (unavailableProductsArray.length > 0) {
        modalInterface.addNotAvailableSection(unavailableProductsArray);

        // For number of unavailable products exceeding _maxProductsInARow value — view all link is added
        if (unavailableProductsArray.length > MAX_PRODUCTS_IN_ROW) {
          const notAvailableItemsListContainer = document.querySelector(
            '.retail-not-available__items',
          );

          if (isDesktop() && !notAvailableItemsListContainer.dataset.height) {
            toggleNotAvailableItems(notAvailableItemsListContainer);
          }

          onTabletToDesktop(() => {
            if (!notAvailableItemsListContainer.dataset.height) {
              toggleNotAvailableItems(notAvailableItemsListContainer);
            }
          });

          onDesktopMediaChange(checkViewUnavailableState);
        }
      }
    } else if (!data.RetailerCarts || !data.RetailerCarts.length) {
      modalInterface.renderNoResults(_labels.ct_labels_no_results);
    } else {
      log.warn(`Unknown status: ${data.Status}`);
    }
  } else if (!data.CartId && data.Status) {
    switch (data.Status) {
      case STATUS_CT_NO_RETAILERS:
      case STATUS_CT_NO_PRODUCTS:
        modalInterface.renderNoResults(_labels.ct_labels_no_results);
        break;
      case STATUS_CT_INVALID_REQUEST:
        log.error(`Invalid request. Data: ${data}`);
        break;
      case STATUS_CT_FAILED:
        log.warn(`Failed request. Data: ${data}`);
        break;
      default:
        log.warn(`Unknown status: ${data.Status}`);
    }
  } else {
    log.warn(`No status received. Data: ${data}`);
  }
}

/**
 * Log error info into console. The errors from appendix from Channel Sight.
 *
 * @param {object} responseData
 * @param {number} responseData.status
 * @param {string} responseData.responseText
 */
function searchError(responseData) {
  const { status: errorCode, responseText: responseMessage } = responseData;

  let message = 'Additional info: \n';

  switch (errorCode) {
    case HTTP_STATUS_CODE.notFound:
      message +=
        'This error will be rare, but may occur during platform updates for a very limited ' +
        'time. Such responses are usually instantaneous (i.e. less than a couple of seconds, taking ' +
        'into account network latency).';
      break;
    case HTTP_STATUS_CODE.serverError:
      message +=
        'If the platform is experiencing a malfunction, the server might respond with a HTTP ' +
        'Status 500 message. Under most circumstances the server will respond within a couple ' +
        'of seconds, but there’s a small subset of cases where the response may need ' +
        'significantly longer time. This rare case will need to be dealt akin to a Timeouts case.';
      break;
    case HTTP_STATUS_CODE.serverBusy:
      message +=
        'If the platform is overloaded due to high usage, the server may respond with a Server' +
        ' Busy message. Under most circumstances the server will respond within a couple of ' +
        'seconds, but there’s a small subset of cases where the response may need significantly ' +
        'longer time. This rare case will need to be dealt akin to a Timeouts case.';
      break;
    case HTTP_STATUS_CODE.badRequest:
      if (!responseMessage) {
        break;
      }

      switch (responseMessage.Status) {
        case STATUS_CT_NO_RETAILERS:
        case STATUS_CT_NO_PRODUCTS:
          modalInterface.renderNoResults(_labels.ct_labels_no_results);
          message += `Invalid request. Message: ${responseMessage.Message}`;
          break;
        case STATUS_CT_INVALID_REQUEST:
          message += `Invalid request. Message: ${responseMessage.Message}`;
          break;
        case STATUS_CT_FAILED:
          message += `Failed request. Message: ${responseMessage.Message}`;
          break;
        default:
          message += `Unknown status: ${responseMessage.Status}`;
      }

      break;
    default:
      message += 'Unknown status code';
  }

  log.error(`Resource respond with status: ${errorCode}\n${message}`);
}
